import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Input, Select } from 'antd';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { API_CARD_DEFS_URL, isAdmin } from '../../utils';
import { canManage } from '../../utils/permissions';
import { AdminJSON } from '../../utils/admin';
import { normalizeId } from '../../utils/ids';
import { and as makeAnd, cmp as makeCmp, serialize, str as makeStr } from '../../utils/kql';
import { parseForBuilder, readSearchTerm, withSearchTerm } from '../../utils/kqlBuilder';
import { getCardScope, isRetiredCard } from '../../utils/cardScopes';
import { retireCard, unretireCard } from '../../services/cardDefs';
import { buildCardFilterFields } from '../../utils/listFilterFields';
import { getFieldValue, parseDateValue } from '../../utils/sort';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useCardList from '../../hooks/useCardList';
import { useListQuery } from '../../hooks/useListQuery';
import useUrlParams from '../../hooks/useUrlParams';
import { LOCK, MAGNIFYING_GLASS } from '../icons';
import ExportModal from '../common/ExportModal';
import ConfirmWordModal from '../common/ConfirmWordModal';
import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import CardDefinitionGrid from './CardDefinitionGrid';
import ElementSetList from '../elementSets/ElementSetList';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { useCollections } from '../../contexts/CollectionsContext';
import RecordBadge from '../common/RecordBadge';
import { cardPagePath } from '../../utils/cards';
import { orderWithSubCollections, parentKicker } from '../../utils/collectionFamily';
import { collectionCardsPath } from '../../config/collectionShell';

const SEARCH_DEBOUNCE_MS = 250;

/** One select carries field AND direction; the URL carries them apart. */
export const SORT_OPTIONS = [
	{ value: 'name:asc', label: 'Name A–Z' },
	{ value: 'name:desc', label: 'Name Z–A' },
	{ value: 'createdAt:desc', label: 'Newest' },
	{ value: 'createdAt:asc', label: 'Oldest' },
	{ value: 'updatedAt:desc', label: 'Recently updated' },
	{ value: 'children:desc', label: 'Most children' },
];

const SORT_PARAMS = { sort: { default: 'name', trim: false }, dir: { default: 'asc', trim: false } };
const DEFAULT_SORT_KEY = `${SORT_PARAMS.sort.default}:${SORT_PARAMS.dir.default}`;

/**
 * `?sort=` / `?dir=` for the cards list, the same idiom as the items page
 * (hooks/useUrlParams). Defaults are omitted from the URL so an unsorted link
 * stays canonical; a pair that is not one of the offered options (a
 * hand-edited link) reads as the default rather than as a blank select.
 */
function useCardSortParams() {
	const { params: { sort, dir }, update } = useUrlParams(SORT_PARAMS);
	const combined = `${sort}:${dir}`;
	const sortKey = SORT_OPTIONS.some(option => option.value === combined) ? combined : DEFAULT_SORT_KEY;

	const setSortKey = useCallback(value => {
		const splitAt = value.lastIndexOf(':');
		update({ sort: value.slice(0, splitAt), dir: value.slice(splitAt + 1) });
	}, [update]);

	return { sortKey, setSortKey };
}

const byName = (a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });
const byId = (a, b) => String(normalizeId(a?._id) || '').localeCompare(String(normalizeId(b?._id) || ''));

// dayjs(undefined) is "now", so a missing timestamp must be caught first.
const timeOf = (card, field) => {
	const raw = getFieldValue(card, field);
	return raw ? (parseDateValue(raw) ?? 0) : 0;
};

const childCountOf = (card, fallbackCounts) => {
	const fromUsage = card?.usage?.childCount;
	if (Number.isFinite(fromUsage)) return fromUsage;
	return fallbackCounts.get(normalizeId(card?._id)) || 0;
};

/**
 * Client-side ordering — the API returns the list unsorted by design. Ties
 * (the same child count, the same day) fall back to name, then id, so the
 * grid never reshuffles between two renders of the same data.
 */
export function sortCards(cards, sortKey) {
	const splitAt = sortKey.lastIndexOf(':');
	const field = sortKey.slice(0, splitAt);
	const sign = sortKey.slice(splitAt + 1) === 'desc' ? -1 : 1;

	// An API without `usage` yet: count children here, as the grid does.
	const fallbackCounts = new Map();
	if (field === 'children') {
		(cards || []).forEach(card => {
			const parentId = normalizeId(card?.parentCardId);
			if (parentId) fallbackCounts.set(parentId, (fallbackCounts.get(parentId) || 0) + 1);
		});
	}

	const primary = (a, b) => {
		if (field === 'name') return byName(a, b);
		if (field === 'children') return childCountOf(a, fallbackCounts) - childCountOf(b, fallbackCounts);
		return timeOf(a, field) - timeOf(b, field);
	};

	return [...(cards || [])].sort((a, b) => (sign * primary(a, b)) || byName(a, b) || byId(a, b));
}

export const NOT_IN_A_COLLECTION = 'Not in a collection';

const byLabel = (a, b) => (a?.label || '').localeCompare(b?.label || '', undefined, { sensitivity: 'base' });

/**
 * The groups the cards index renders, in order (KPR-1286, ruling q10 of
 * KPR-1276): keepr's Global cards; then one group per collection that owns
 * cards, in the collections list's reading order (a sub-collection right
 * after its parent), owners the list does not hold after them by name; last,
 * unassigned and legacy cards, which belong to no collection. Each group
 * keeps the list's sort, and only groups with cards are made.
 *
 * A collection group carries `collection`, the owner's identity: the
 * collections list's row when it is there (it knows the parent), else the
 * card's `usage.ownerCollection` summary, else the bare id.
 */
export function groupCardsByOwner(cards, collections = []) {
	const global = [];
	const loose = [];
	const byOwner = new Map();
	(cards || []).forEach(card => {
		const scope = getCardScope(card);
		if (scope === 'global') { global.push(card); return; }
		const owner = scope === 'collection' ? normalizeId(card?.collection_id) : null;
		if (!owner) { loose.push(card); return; }
		if (!byOwner.has(owner)) byOwner.set(owner, { cards: [], summary: null });
		const entry = byOwner.get(owner);
		entry.cards.push(card);
		if (!entry.summary && card?.usage?.ownerCollection) entry.summary = card.usage.ownerCollection;
	});

	const collectionGroup = (id, collection) => ({
		key: `collection:${id}`,
		label: (collection?.name || '').trim() || 'Untitled collection',
		collection: collection || { _id: id },
		cards: byOwner.get(id).cards,
	});
	const listed = [];
	const placed = new Set();
	orderWithSubCollections(Array.isArray(collections) ? collections : []).forEach(collection => {
		const id = normalizeId(collection?._id);
		if (!id || placed.has(id) || !byOwner.has(id)) return;
		placed.add(id);
		listed.push(collectionGroup(id, collection));
	});
	const unlisted = [...byOwner.keys()]
		.filter(id => !placed.has(id))
		.map(id => {
			const summary = byOwner.get(id).summary;
			return collectionGroup(id, summary ? { ...summary, _id: id } : null);
		})
		.sort(byLabel);

	return [
		{ key: 'global', label: 'Global', cards: global },
		...listed,
		...unlisted,
		{ key: 'none', label: NOT_IN_A_COLLECTION, cards: loose },
	].filter(group => group.cards.length);
}

/** A collection group's header: its collection's badge, a sub-collection's carrying its parent's name. */
const withOwnerBadges = groups => groups.map(group => (group.collection ? {
	...group,
	badge: (
		<RecordBadge
			kind="collection"
			record={group.collection}
			title={group.label}
			kicker={parentKicker(group.collection) || undefined}
			size="sm"
			to={collectionCardsPath(normalizeId(group.collection._id))}
			className="card-def-group__badge"
		/>
	),
} : group));

// ---- The Global chip: sugar over ONE condition, `scope = global` ----
// "Top level" as for the search term: the root `cmp` or a direct child of a
// root `and`. A scope condition nested under or/not belongs to the builder.
const isGlobalCondition = node =>
	!!node
	&& node.op === 'cmp'
	&& node.cmp === 'eq'
	&& !node.forceElement
	&& Array.isArray(node.path)
	&& node.path.length === 1
	&& node.path[0] === 'scope'
	&& node.value?.v === 'global';

const topLevelNodes = ast => {
	if (!ast) return [];
	if (ast.op === 'and' && Array.isArray(ast.args)) return ast.args;
	return [ast];
};

/** True when the query carries a top-level `scope = global`. */
export function readGlobalOnly(ast) {
	return topLevelNodes(ast).some(isGlobalCondition);
}

/**
 * The query with every top-level `scope = global` removed and, when `on`,
 * one appended to the root AND (a non-AND root is wrapped). Returns null
 * when nothing remains. Pure: the input AST is never mutated.
 */
export function withGlobalOnly(ast, on) {
	const kept = topLevelNodes(ast).filter(node => !isGlobalCondition(node));
	if (on) kept.push(makeCmp(['scope'], 'eq', makeStr('global')));
	if (!kept.length) return null;
	if (kept.length === 1) return kept[0];
	return makeAnd(kept);
}

/**
 * The global card list. Each tile carries the card-definition action set in
 * the platform taxonomy: open (navigate), export (share), and — for admins,
 * who administer global cards — delete. Collection managers delete their
 * own cards from the Card Library, where the collection is in hand.
 *
 * The LIST comes from `useCardList` (filtered by the URL's `?q=`, annotated
 * with usage); the app-wide cache is still refreshed on mount because it
 * feeds the filter vocabulary (parent-card choices) and every other consumer.
 */
export default function ViewCards() {
	const navigate = useNavigate();
	const { message, modal } = App.useApp();
	const { cards: contextCards, elementSets = [], refresh } = useCardDefinitions();
	const [exportCard, setExportCard] = useState(null);
	// The card whose delete is being confirmed: the card page's Danger zone
	// dialog (CardDangerZone), armed on the typed word, from this door too.
	const [deletingCard, setDeletingCard] = useState(null);
	const [deleteBusy, setDeleteBusy] = useState(false);
	const admin = isAdmin();

	// Definitions are cached app-wide; refresh on mount so pickers and the
	// vocabulary always reflect edits made elsewhere.
	useEffect(() => {
		refresh();
	}, [refresh]);

	// ---- URL state: ?q= (KQL) and ?sort=/?dir= ----
	const { query, setQuery } = useListQuery();
	const { sortKey, setSortKey } = useCardSortParams();

	// A query the server refuses (a stale link, an API without the cards
	// subject yet) is dropped rather than left to fail on every render.
	const handleQueryError = useCallback(serverMessage => {
		message.error(serverMessage || 'That filter could not be read, so it was removed.');
		setQuery(undefined);
	}, [message, setQuery]);

	const {
		cards: listCards,
		totalCount,
		loading,
		error: listError,
		refresh: refreshList,
	} = useCardList({ query, onQueryError: handleQueryError });

	// ---- Filter vocabulary ----
	// The collections the caller can see (the shared list), for the "Used in collection"
	// picker. Failing silently leaves that picker empty; nothing else needs it.
	const { collections } = useCollections();

	const filterFields = useMemo(
		() => buildCardFilterFields({ cards: contextCards, collections, elementSets }),
		[contextCards, collections, elementSets]
	);
	// The collections this viewer manages: whose element sets the section
	// below may offer to edit and delete.
	const manageableCollectionIds = useMemo(
		() => collections.filter(canManage).map(collection => normalizeId(collection?._id)).filter(Boolean),
		[collections]
	);

	// ---- The search box: the query's top-level `text ~ …` condition ----
	const { ast } = useMemo(() => parseForBuilder(query), [query]);
	const urlSearchTerm = useMemo(() => readSearchTerm(ast, 'text'), [ast]);
	const [searchText, setSearchText] = useState(urlSearchTerm);
	const searchRef = useRef(null);
	const debounceRef = useRef(null);
	const astRef = useRef(ast);
	const searchTextRef = useRef(searchText);
	useEffect(() => { astRef.current = ast; }, [ast]);
	useEffect(() => { searchTextRef.current = searchText; }, [searchText]);

	// The URL is the source of truth: a dismissed chip, a builder Apply, or a
	// dropped query re-seeds the box — but never mid-keystroke (a pending
	// debounce), and never to trim a trailing space the user is still typing
	// past.
	useEffect(() => {
		if (debounceRef.current) return;
		if (searchTextRef.current.trim() === urlSearchTerm) return;
		setSearchText(urlSearchTerm);
	}, [urlSearchTerm]);

	const commitSearch = useCallback(term => {
		clearTimeout(debounceRef.current);
		debounceRef.current = null;
		const next = withSearchTerm(astRef.current, term, 'text');
		setQuery(next ? serialize(next) : undefined);
	}, [setQuery]);

	const handleSearchChange = useCallback(event => {
		const value = event.target.value;
		setSearchText(value);
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => commitSearch(value), SEARCH_DEBOUNCE_MS);
	}, [commitSearch]);

	useEffect(() => () => clearTimeout(debounceRef.current), []);

	// ---- The Global chip ----
	const globalOnly = useMemo(() => readGlobalOnly(ast), [ast]);
	const toggleGlobalOnly = useCallback(() => {
		const next = withGlobalOnly(astRef.current, !readGlobalOnly(astRef.current));
		setQuery(next ? serialize(next) : undefined);
	}, [setQuery]);

	// The platform keyboard map's `/`: focus the filter — here, the search box.
	useEffect(() => {
		const handler = (event) => {
			if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
			const target = event.target;
			if (target instanceof Element) {
				if (target.closest('input, textarea, select, [contenteditable="true"], .ant-modal, .ant-popover, .ant-drawer, .ant-select')) return;
			}
			event.preventDefault();
			searchRef.current?.focus();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// ---- Ordering and grouping ----
	const sortedCards = useMemo(() => sortCards(listCards, sortKey), [listCards, sortKey]);
	const groups = useMemo(() => withOwnerBadges(groupCardsByOwner(sortedCards, collections)), [sortedCards, collections]);

	// ---- Actions ----
	const handleCardSelect = useCallback((card) => {
		if (!card?._id) return;
		// A collection card opens in its collection; a global, unassigned or
		// legacy card on the platform page.
		navigate(cardPagePath(card));
	}, [navigate]);

	// /cards/new without a collection creates a GLOBAL card — admin only.
	// Everyone else creates cards from a collection's Card Library, so the
	// button would only lead them to the blocked-create explainer.
	const handleCreateCard = useCallback(() => {
		navigate('/cards/new');
	}, [navigate]);

	const deleteCard = useCallback((card) => {
		if (normalizeId(card?._id)) setDeletingCard(card);
	}, []);

	const confirmDeleteCard = useCallback(async () => {
		const id = normalizeId(deletingCard?._id);
		if (!id) return;
		setDeleteBusy(true);
		try {
			await api.delete(`${API_CARD_DEFS_URL}/${id}`);
			message.success('Card deleted.');
			setDeletingCard(null);
			// The list AND the app-wide cache: pickers elsewhere must
			// stop offering the card too.
			refreshList();
			refresh();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Delete failed.');
		} finally {
			setDeleteBusy(false);
		}
	}, [deletingCard, message, refresh, refreshList]);

	// Opening a card lands on its HOME; editing is the deep link to the first
	// editing tab (docs/plans/card-editor-dates-groups-filters.md Part 3).
	const editCard = useCallback((card) => {
		const id = normalizeId(card?._id);
		if (id) navigate(cardPagePath(card, null, 'tab=basics'));
	}, [navigate]);

	// RETIRE / RETURN (KPR-280, ruling G14: retiring replaces deleting a
	// global card in use) — an admin's act on a global card, at once.
	const toggleRetired = useCallback(async (card) => {
		const id = normalizeId(card?._id);
		if (!id) return;
		const retired = isRetiredCard(card);
		try {
			await (retired ? unretireCard(id) : retireCard(id));
			message.success(retired ? 'The card is back in the Card Library.' : 'Card retired. It has left the Card Library; every collection that uses it keeps working.');
			refreshList();
			refresh();
		} catch (error) {
			message.error(error?.response?.data?.message || (retired ? 'The card was not returned to the Card Library.' : 'The card was not retired.'));
		}
	}, [message, refresh, refreshList]);
	const confirmRetire = useCallback((card) => {
		if (isRetiredCard(card)) { toggleRetired(card); return; }
		const name = (card?.name || '').trim() || 'this card';
		modal.confirm({
			title: 'Retire this card?',
			content: `${name} leaves the Card Library: no collection can add it and no new card can be built on it. Every collection that uses it keeps working, and you can return it to the library later.`,
			okText: 'Retire',
			cancelText: 'Keep it',
			onOk: () => toggleRetired(card),
		});
	}, [modal, toggleRetired]);

	const cardActions = useCallback((card) => {
		const global = getCardScope(card) === 'global';
		// A global card in use is retired, never deleted (the API answers 409
		// card_in_use): its Delete gives way when the API said it is in use.
		const inUse = global && card?.usage?.inUse?.inUse === true;
		// Offered when the API speaks of it (an older API sends no `inUse` and
		// has no retire endpoint), or to put a retired card back.
		const canRetire = admin && global && (isRetiredCard(card) || typeof card?.usage?.inUse?.inUse === 'boolean');
		return [
			{ key: 'open', group: 'navigate', glyph: 'open', label: 'Open card definition', onClick: handleCardSelect },
			// A global card is keepr's to edit; everyone else extends it.
			(admin || !global)
				? { key: 'edit', group: 'navigate', glyph: 'edit', label: 'Edit card', onClick: editCard }
				: null,
			{ key: 'export', group: 'share', glyph: 'export', label: 'Export…', onClick: setExportCard },
			canRetire
				? { key: 'retire', group: 'record', glyph: 'archive', label: isRetiredCard(card) ? 'Return to the Card Library' : 'Retire card…', onClick: confirmRetire }
				: null,
			(admin && !inUse) ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete card…', onClick: deleteCard } : null,
		].filter(Boolean);
	}, [admin, handleCardSelect, editCard, deleteCard, confirmRetire]);

	// ---- The list route's filter row ----
	// Nothing held yet and a read in flight: the first load. A later read keeps the cards on screen.
	const firstLoad = loading && !listCards.length;

	const toolbar = (
		<div className="list-toolbar">
			<Input
				{...NO_TEXT_ASSIST}
				ref={searchRef}
				className="list-toolbar__search"
				allowClear
				prefix={<MAGNIFYING_GLASS />}
				placeholder="Search cards…"
				aria-label="Search cards"
				value={searchText}
				onChange={handleSearchChange}
				onPressEnter={() => commitSearch(searchTextRef.current)}
			/>

			{query ? (
				<FilterChips
					query={query}
					fields={filterFields}
					onChange={setQuery}
					hiddenPaths={['text']}
				/>
			) : null}

			<button
				type="button"
				className={`view-chip${globalOnly ? ' view-chip--active' : ''}`}
				aria-pressed={globalOnly}
				onClick={toggleGlobalOnly}
			>
				<LOCK aria-hidden="true" />
				Global
			</button>

			<FilterBuilder
				fields={filterFields}
				query={query}
				onApply={setQuery}
				triggerId="cards-filter-trigger"
				emptyText="No conditions yet — every card you can see is shown."
				placeholder="element.type = number and parent = person"
			/>

			<span className="list-toolbar__divider" aria-hidden="true" />

			<Select
				className="list-toolbar__sort"
				value={sortKey}
				onChange={setSortKey}
				options={SORT_OPTIONS}
				popupMatchSelectWidth={false}
				aria-label="Sort cards"
			/>

			<span className="list-toolbar__spacer" />

			<span className="list-toolbar__count">
				{firstLoad ? null : (totalCount === 1 ? '1 card' : `${totalCount} cards`)}
			</span>
		</div>
	);

	// A filtered list with nothing in it offers the one thing that helps;
	// an unfiltered one keeps the grid's own "no card definitions yet".
	const emptyContent = (!loading && !sortedCards.length && listError) ? (
		// A failed read is not an empty library: say so, and offer Retry (§14).
		<div className="list-empty" role="alert">
			<span>Unable to load cards.</span>
			<Button onClick={refreshList}>Retry</Button>
		</div>
	) : (!loading && !sortedCards.length && query) ? (
		<div className="list-empty">
			<span>No cards match</span>
			<Button onClick={() => setQuery(undefined)}>Clear filters</Button>
		</div>
	) : undefined;

	return (
		<>
			<CardDefinitionGrid
				groups={groups}
				loading={firstLoad}
				// The toolbar's "N cards" is the count; the title row shows none.
				headerSubtitle=""
				toolbar={toolbar}
				emptyContent={emptyContent}
				onCardSelect={handleCardSelect}
				onCreateCard={admin ? handleCreateCard : undefined}
				cardActions={cardActions}
			/>

			{/* Every element set the caller can read, beneath the cards they
			    attach to (docs/plans/element-sets-and-me.md). */}
			<div className="view-cards__element-sets">
				<ElementSetList manageableCollectionIds={manageableCollectionIds} />
			</div>

			<ExportModal
				open={Boolean(exportCard)}
				onClose={() => setExportCard(null)}
				target={exportCard ? { kind: 'card', cardId: normalizeId(exportCard._id) } : null}
				title="Export card definition"
				baseName={exportCard?.name || 'card'}
			/>

			<ConfirmWordModal
				open={Boolean(deletingCard)}
				word="delete"
				title="Delete this card?"
				okText="Delete"
				busy={deleteBusy}
				onOk={confirmDeleteCard}
				onCancel={() => setDeletingCard(null)}
			>
				<p>
					Deleting <strong>{(deletingCard?.name || '').trim() || 'this card'}</strong> leaves existing items with their data,
					but no new item can use the card. You can restore it from Recently deleted.
				</p>
			</ConfirmWordModal>

			<AdminJSON obj={sortedCards} title="cards" />
		</>
	);
}
